import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  DEFAULT_SETTINGS,
  addMoney,
  centavos,
  formatMoney,
  formatQuantity,
  hoy,
  lineTotal,
  newId,
  parseMoney,
  ZERO,
  ZERO_COST,
  fromUnits,
  type Centavos,
  type Milli,
  type UnitCost,
} from '@abastece/shared';
import type { Cliente, Pedido, Producto, SesionCaja, UnidadVenta } from '@abastece/core';
import { existenciaId } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { emitir, emitirYVer } from '../lib/emitir.js';
import { cubreLaFalta, cubreLoApartado, factoresDe, faltaParaLaVenta, hayEnLaTienda, tomaDeLoApartado } from '../lib/apartados.js';
import { enPractica } from '../lib/practica.js';
import { esperarRespuesta, suscribirSync, type SyncPhase } from '../sync/engine.js';
import { sesionAbierta, siguienteTicket } from '../lib/caja.js';
import { etiquetaCortaLista, etiquetaListaPrecio, listaPredeterminada, ordenarListas, precioDe, unidadesDe } from '../lib/catalogo.js';
import { leerEnvio, textoDeEnvio } from '../lib/envio.js';
import { clienteLlamado, lineasDelPedido, totalConEnvio } from '../lib/pedidos.js';
import { imprimirTexto } from '../lib/imprimir.js';
import { abrirPantallaCliente, usePublicarPantalla, type EstadoPantalla } from '../lib/pantalla-cliente.js';
import { datosNegocio, enlaceWhatsApp, textoRecibo } from '../lib/recibo.js';
import { coincide } from '../lib/texto.js';
import { useSesion, usePuede } from '../lib/sesion.js';
import { llegaConTransicion } from '../lib/transiciones.js';
import { atrasHasta, irA } from '../lib/volver.js';
import { BuscadorProducto } from '../ui/BuscadorProducto.js';
import { Campo } from '../ui/Campo.js';
import { CampoEnvio } from '../ui/CampoEnvio.js';
import { EditorLinea, type LineaEditable } from '../ui/EditorLinea.js';
import {
  CLIENTE_MOSTRADOR,
  FormularioCliente,
  camposDe,
  type BorradorCliente,
} from '../ui/FormularioCliente.js';
import { coincideTelefono, formatearTelefono } from '../lib/telefono.js';
import { Modal } from '../ui/Modal.js';
import { BotonVolver } from '../ui/Pagina.js';
import { AbonoModal } from '../ui/Abono.js';
import { PanelCobro, type PagoBorrador } from '../ui/PanelCobro.js';
import { PedirPin } from '../ui/PedirPin.js';
import { Confirmar } from '../ui/Preguntar.js';
import { useT } from '../i18n/index.js';
import { etiquetaPresentacion, etiquetaUnidad } from '../i18n/dominio.js';
import { useNombreProducto } from '../i18n/productos.js';
import { Ico } from '../ui/Iconos.js';
import { LineaDeCarrito, LineaEscaneada, type ListasDeLinea, type PresentacionesDeLinea } from '../ui/LineaDeCarrito.js';
import { useCajaDeOtro } from '../ui/RecibirCaja.js';
import { useEmpiezaElDia } from '../ui/LoDelDia.js';
import { VerMasAlBajar } from '../ui/VerMasAlBajar.js';
import { costoConocido, enRango } from '../lib/costos.js';
import { vencidosEn } from '../lib/vencimientos.js';
import { ahorroEnCaja, gratisEnCaja, nombreDeLleva, precioEnCaja, subtotalEnCaja, type Lleva, type PrecioEnCaja } from '../lib/ofertas.js';

/** How long a sale waits for the server's word before it goes through unconfirmed (`venderSoloLoQueHay`). */
const ESPERA_VENTA_MS = 5_000;

/** The clients the sheet draws at a time, the next as its foot comes near (`VerMasAlBajar`). */
const CLIENTES_A_LA_VEZ = 30;
/** The orders to deliver over them, and as many more at each «Ver más». */
const PEDIDOS_A_LA_VEZ = 12;

interface LineaCarrito extends LineaEditable {
  key: string;
  productoId: string | null;
  /**
   * The list the line's `precioLista` came from (2026-10-11): each line has its own, the sale's
   * when it went in. Absent (an order's lines, a sale kept from before), the sale's.
   */
  listaId?: string;
  autorizadoPor: string | null;
  /** The owner's yes came with an order, for its price (`lineasDelPedido`): not for expired medicine. */
  autorizadoEnElPedido?: true;
  /**
   * The offer its list price is (`lib/ofertas.ts`, 2026-11-03), and the list's own price without it:
   * the line went in, or moved to another list or presentation, while one ran.
   */
  ofertaId?: string;
  precioRegular?: Centavos;
  /** That offer's so many for fewer, «2×1»: at the list's price, the free ones taken off its total (`subtotalEnCaja`). */
  lleva?: Lleva;
}

/** The offer a price came from, for its line: which, the list's own price, and its so many for fewer; nothing without one. */
const deLaOferta = (p: PrecioEnCaja | null): Pick<LineaCarrito, 'ofertaId' | 'precioRegular' | 'lleva'> =>
  p?.ofertaId
    ? { ofertaId: p.ofertaId, ...(p.precioRegular !== undefined ? { precioRegular: p.precioRegular } : {}), ...(p.lleva ? { lleva: p.lleva } : {}) }
    : {};

/** A line without its offer, before it takes another price. */
function sinOferta(l: LineaCarrito): LineaCarrito {
  const { ofertaId: _oferta, precioRegular: _regular, lleva: _lleva, ...resto } = l;
  return resto;
}

/**
 * The sale being rung up, kept in this tab while the till is out of sight.
 *
 * «Volver», «Historial» and «Caja» sit a thumb away from a half-rung sale, and each of them threw
 * it away: checking a price in the history, or how much is in the drawer, meant ringing the whole
 * thing up again. Now the sale is where it was when the till comes back, through a reload too.
 *
 * Kept per person. Whoever signs in next on this phone starts with an empty till, not with
 * somebody else's sale and whatever the owner said yes to on it.
 */
const CLAVE_BORRADOR = 'abastece.venta-en-curso';

interface Borrador {
  usuarioId: string;
  carrito: LineaCarrito[];
  listaId: string;
  clienteId: string | null;
  pedidoId: string | null;
  /** The order was sent from «Pedidos»: charged, the till goes back there. */
  desdePedidos?: boolean;
  listaAutorizadaPor: string | null;
  /** The lists that owner said yes to. Absent in a sale kept from before: the sale's own. */
  listasAutorizadas?: string[];
  /** «Envío» as typed above the total. Absent in a sale kept from before: none. */
  envio?: string;
}

function leerBorrador(usuarioId: string | undefined): Borrador | null {
  if (!usuarioId) return null;
  try {
    const b = JSON.parse(sessionStorage.getItem(CLAVE_BORRADOR) ?? 'null') as Borrador | null;
    return b && b.usuarioId === usuarioId && Array.isArray(b.carrito) ? b : null;
  } catch {
    return null;
  }
}

function guardarBorrador(b: Borrador): void {
  try {
    if (b.carrito.length === 0 && !b.clienteId && !b.envio) sessionStorage.removeItem(CLAVE_BORRADOR);
    else sessionStorage.setItem(CLAVE_BORRADOR, JSON.stringify(b));
  } catch {
    // Nowhere to keep it: leaving the till loses the sale, as it always did.
  }
}

/** Seven or more digits, with the spaces and dashes people write them with. */
const pareceTelefono = (texto: string) => /^\+?[\d\s-]{7,}$/.test(texto.trim());

/**
 * A new client starts from what was typed in the search: a name goes in the name, a number in
 * the phone. Whoever searched for them and found nobody should not have to type it twice.
 */
function borradorDesdeBusqueda(texto: string): BorradorCliente {
  const escrito = texto.trim();
  if (!escrito) return CLIENTE_MOSTRADOR;
  return pareceTelefono(escrito)
    ? { ...CLIENTE_MOSTRADOR, telefono: formatearTelefono(escrito) }
    : { ...CLIENTE_MOSTRADOR, nombre: escrito };
}

export function Pos() {
  const t = useT();
  const nombre = useNombreProducto();
  const navigate = useNavigate();
  const sesion = useSesion();
  // Arriving by a view transition, the transition is the entrance; otherwise the old fade.
  const [entrada] = useState(() => (llegaConTransicion() ? '' : 'aparece'));
  const puedeCambiarPrecio = usePuede('precio_bajo_minimo');
  const puedeEditarPrecio = usePuede('precio_override');
  /** The product's own price, kept: the owner's (2026-10-13). A cashier types one for this sale only. */
  const puedePrecios = usePuede('editar_precios');
  const verCostos = usePuede('ver_costos');
  const puedeClientes = usePuede('gestionar_clientes');
  const puedeDarCredito = usePuede('dar_credito');
  /** A client's own list: setting it on a new client is the owner's. */
  const puedeElegirLista = usePuede('elegir_lista');
  /** Another list for this sale or a line of it, without the owner's PIN: the owner and a cashier. */
  const puedeOtraLista = usePuede('cobrar_otra_lista');
  /**
   * The owner's yes at the till, which the owner gives with a tap and anybody else asks for with
   * the owner's PIN (`autorizarDueno`): selling a medicine with expired units, and selling what
   * orders set aside or more than is free. It went with adjusting the stock until a cashier could
   * adjust it too (the owner, 2026-10-09): counting and taking out are a cashier's now, and these
   * stay the owner's, as the owner asked for them («Stop; owner's PIN lets it through»).
   */
  const delDueno = sesion?.rol === 'dueno';
  const puedeVenderVencido = delDueno;
  const puedeVenderApartado = delDueno;

  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const listas = useLiveQuery(() => db.listas.toArray(), [], []);
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], []);
  const precios = useLiveQuery(() => db.precios.toArray(), [], []);
  /** Undefined until read: a sale kept from before keeps its offers until there is something to hold them to. */
  const ofertasLeidas = useLiveQuery(() => db.ofertas.toArray(), []);
  const ofertas = ofertasLeidas ?? [];
  const existencias = useLiveQuery(() => db.existencias.toArray(), [], []);
  /** For a product with no stock anywhere yet: what it cost the last time it was bought. */
  const compras = useLiveQuery(() => db.compras.toArray(), [], []);
  const caja = useLiveQuery(() => sesionAbierta(), [], undefined);
  /**
   * Somebody else's drawer: selling from it would put this person's sales in that person's
   * count. It is received first, counted (`caja.entregada`); the snapshot keeps the result on
   * screen once it has become this person's.
   */
  const cajaDeOtro = useCajaDeOtro();
  const empiezaElDia = useEmpiezaElDia();
  const clientes = useLiveQuery(() => db.clientes.toArray(), [], []);
  const pedidos = useLiveQuery(() => db.pedidos.where('estado').equals('pendiente').toArray(), [], []);
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);

  /** The sale this person left half-rung when the till went out of sight, if they did. */
  const [borrador] = useState(() => leerBorrador(sesion?.usuarioId));
  const [carrito, setCarrito] = useState<LineaCarrito[]>(() => borrador?.carrito ?? []);
  const [listaId, setListaId] = useState<string>(() => borrador?.listaId ?? '');
  const [editando, setEditando] = useState<string | null>(null);
  /** The line «¿Quitar de la venta?» is asking about: from its bin, or «Quitar» in its editor. */
  const [porQuitar, setPorQuitar] = useState<string | null>(null);
  const [cobrando, setCobrando] = useState(false);
  /** The cash rounding «Cobrar» has come to, for the customer's screen (`PanelCobro.onRedondeo`). */
  const [redondeoCobro, setRedondeoCobro] = useState<Centavos>(ZERO);
  const [pidiendoPin, setPidiendoPin] = useState<{ key: string; cambios: Partial<LineaEditable> } | null>(null);
  /** Another list, for the whole sale or for one line of it (`key`), waiting on the owner's PIN. */
  const [pidiendoLista, setPidiendoLista] = useState<{ listaId: string; key?: string } | null>(null);
  /** The owner who let this sale go at a list that is not its own, the whole of it or some lines. */
  const [listaAutorizadaPor, setListaAutorizadaPor] = useState<string | null>(() => borrador?.listaAutorizadaPor ?? null);
  /**
   * The lists that owner said yes to on this sale: one yes, and the rest of the sale may use that list,
   * line by line or all of it, without asking again, as one yes let the whole sale go at it before
   * each line had a list of its own.
   */
  const [listasAutorizadas, setListasAutorizadas] = useState<string[]>(
    () => borrador?.listasAutorizadas ?? (borrador?.listaAutorizadaPor && borrador.listaId ? [borrador.listaId] : []),
  );
  /** «Cobrar» on quick sales past what a cashier may ring alone, waiting on the owner's PIN. */
  const [pidiendoRapidas, setPidiendoRapidas] = useState(false);
  /** «Cobrar» on a medicine with expired units here, waiting on the owner's PIN. */
  const [pidiendoVencida, setPidiendoVencida] = useState(false);
  /** «Cobrar» on goods orders set aside, waiting on the owner: their PIN, or their own yes. */
  const [pidiendoApartado, setPidiendoApartado] = useState(false);
  /** The owner's yes to selling what orders set aside: who, and up to how much of each product. */
  const [apartadoSi, setApartadoSi] = useState<{ usuarioId: string; hasta: Record<string, number> } | null>(null);
  /** The owner's yes to selling more than is free (`venderSoloLoQueHay`): who, and up to how much of each product. */
  const [faltaSi, setFaltaSi] = useState<{ usuarioId: string; hasta: Record<string, number> } | null>(null);
  /** The sale charged, waiting for the server's word before its receipt. */
  const [confirmando, setConfirmando] = useState(false);
  /** The connection, for that word: the browser's, and what the last sync found. */
  const [conRed, setConRed] = useState(() => navigator.onLine);
  const [fase, setFase] = useState<SyncPhase>('inactivo');
  useEffect(() => {
    const si = () => setConRed(true);
    const no = () => setConRed(false);
    window.addEventListener('online', si);
    window.addEventListener('offline', no);
    const dejar = suscribirSync((estado) => setFase(estado.fase));
    return () => {
      window.removeEventListener('online', si);
      window.removeEventListener('offline', no);
      dejar();
    };
  }, []);
  const [rapida, setRapida] = useState(false);
  const [recibo, setRecibo] = useState<{
    texto: string;
    ticket: string;
    telefono?: string;
    sinConfirmar?: boolean;
    /** It delivered an order sent from «Pedidos»: put away, the receipt goes back to the orders. */
    aPedidos?: boolean;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [clienteId, setClienteId] = useState<string | null>(() => borrador?.clienteId ?? null);
  const [pedidoId, setPedidoId] = useState<string | null>(() => borrador?.pedidoId ?? null);
  /**
   * «Envío», typed above the total (the owner, 2026-10-12): what the customer pays for having it
   * brought, on any sale, at the counter too. An order's starts at what the order says.
   */
  const [envioTexto, setEnvioTexto] = useState(() => borrador?.envio ?? '');
  /**
   * The order on the sale came from «Cobrar» on the list of orders. Charged, its receipt put away,
   * the till goes back there (the owner, 2026-10-08: «when i finish charging a customer for an
   * ORDER on the orders page it should take me back to the Orders page»). Gone with the order.
   */
  const [desdePedidos, setDesdePedidos] = useState(() => borrador?.desdePedidos === true);
  useEffect(() => {
    if (!pedidoId) setDesdePedidos(false);
  }, [pedidoId]);
  const [eligiendoCliente, setEligiendoCliente] = useState(false);
  /** Fiado was chosen with nobody on the sale: once somebody is, go straight back to paying. */
  const [fiarTrasElegir, setFiarTrasElegir] = useState(false);
  /** The payment panel reopens on fiado, since that is what was being done when it closed. */
  const [fiadoPedido, setFiadoPedido] = useState(false);
  const [buscaCliente, setBuscaCliente] = useState('');
  /**
   * Somebody who is not in the file yet, being added from the picker without leaving the sale:
   * what the form starts from. What is typed into it stays in the form (`ClienteNuevoEnCaja`).
   */
  const [clienteNuevo, setClienteNuevo] = useState<BorradorCliente | null>(null);
  /** The client on the sale paying some of what they owe, without leaving the till. */
  const [abonando, setAbonando] = useState(false);
  /** The sale just charged, for the customer's screen to thank them and show their change. */
  const [ultimoCobro, setUltimoCobro] = useState<{ total: Centavos; cambio: Centavos; ahorro?: Centavos } | null>(null);

  const cliente = clientes.find((c) => c.id === clienteId) ?? null;

  // Kept as it changes, so leaving by any door leaves it kept. Somebody else signing in with the
  // till on screen gets it empty: the sale was the last person's.
  const usuarioId = sesion?.usuarioId;
  const deQuien = useRef(usuarioId);
  useEffect(() => {
    if (!usuarioId) return;
    if (deQuien.current !== usuarioId) {
      deQuien.current = usuarioId;
      setCarrito([]);
      setClienteId(null);
      setPedidoId(null);
      setListaId('');
      setListaAutorizadaPor(null);
      setListasAutorizadas([]);
      setApartadoSi(null);
      setFaltaSi(null);
      setEnvioTexto('');
      return;
    }
    guardarBorrador({
      usuarioId,
      carrito,
      listaId,
      clienteId,
      pedidoId,
      desdePedidos,
      listaAutorizadaPor,
      listasAutorizadas,
      ...(envioTexto.trim() ? { envio: envioTexto } : {}),
    });
  }, [usuarioId, carrito, listaId, clienteId, pedidoId, desdePedidos, listaAutorizadaPor, listasAutorizadas, envioTexto]);

  const activas = ordenarListas(listas);
  const listaDeLaTienda = listaPredeterminada(activas)?.id ?? activas[0]?.id ?? '';
  useEffect(() => {
    if (!listaId && listaDeLaTienda) setListaId(listaDeLaTienda);
  }, [listaDeLaTienda, listaId]);
  /**
   * The list this sale gets without asking anybody: the order's, the client's, or the shop's.
   * Any other one takes the owner's PIN from whoever may not charge it alone (`cobrar_otra_lista`,
   * a driver's case since cashiers may, 2026-10-15): its prices match a list, so nothing else would
   * flag a sale rung at wholesale and charged at retail.
   */
  const listaPropia =
    pedidos.find((p) => p.id === pedidoId)?.listaId ?? cliente?.listaId ?? listaDeLaTienda;
  /** A list this sale may go at without asking: its own, anything for the owner, or one the owner said yes to. */
  const sinPedirla = (id: string) => puedeOtraLista || id === listaPropia || listasAutorizadas.includes(id);

  const ubicacion = caja?.ubicacion ?? 'tienda';
  /** A line still at zero is a product nobody priced. Charging it would give the goods away. */
  const sinPrecio = carrito.filter((l) => l.precioUnitario <= 0).length;
  /**
   * The quick sales on this ticket that nobody has said yes to. A venta rápida has no product
   * behind it, so no cost, no stock and no margin: past «Venta rápida sin el dueño», a cashier's
   * «Cobrar» asks for the owner's PIN, which then goes on those lines. Counted over the ticket,
   * so a C$ 450 bottle cannot go through as three quick sales of C$ 150.
   */
  const rapidasSinAutorizar = centavos(
    carrito
      .filter((l) => l.productoId === null && !l.autorizadoPor)
      .reduce((a, l) => a + lineTotal(l.precioUnitario, l.cantidad), 0),
  );
  const rapidasPidenDueno = !puedeCambiarPrecio && rapidasSinAutorizar > config.ventaRapidaMaxima;
  /**
   * Medicine and perishables on this ticket with units past their date at this till's location,
   * with the oldest date. The sale would draw those down first. A perishable is flagged; a
   * medicine takes the owner's yes, which goes on its lines, until the expired units are written
   * off in «Vencimientos».
   */
  const idsEnVenta = [...new Set(carrito.flatMap((l) => (l.productoId ? [l.productoId] : [])))];
  const vencidos = useLiveQuery(
    async () =>
      idsEnVenta.length === 0
        ? new Map<string, string>()
        : vencidosEn(await db.lotes.where('productoId').anyOf(idsEnVenta).toArray(), ubicacion),
    [idsEnVenta.join(','), ubicacion],
    new Map<string, string>(),
  );
  const esMedicina = (id: string | null) => id !== null && productos.find((p) => p.id === id)?.esMedicamento === true;
  const medicinaVencidaSinDueno = carrito.filter(
    (l) => l.productoId && vencidos.has(l.productoId) && esMedicina(l.productoId) && (!l.autorizadoPor || l.autorizadoEnElPedido),
  );
  const vencidaPideDueno = !puedeVenderVencido && medicinaVencidaSinDueno.length > 0;
  /**
   * With `apartarPedidos` on: what this sale takes of what pending orders set aside, product by
   * product, and the orders holding it. Selling it is the owner's call (the owner, 2026-10-08:
   * «Stop; owner's PIN lets it through»): «Cobrar» asks for their PIN, or their own yes when it is
   * them ringing it, and the sale says who. Sold, those orders come up short, and «Pedidos» says so.
   * More of it after the yes asks again.
   */
  const lineasEnBase = carrito.map((l) => ({ productoId: l.productoId, cantidad: l.cantidad, factor: l.unidad?.factor }));
  const factorDe = factoresDe(unidades);
  const hayDe = hayEnLaTienda(existencias);
  const tomaApartado = config.apartarPedidos ? tomaDeLoApartado(lineasEnBase, { pedidos, factorDe, hayDe, pedidoId, ubicacion }) : [];
  const apartadoPideDueno = tomaApartado.length > 0 && !cubreLoApartado(tomaApartado, apartadoSi?.hasta ?? null);
  /**
   * Only what is there (`venderSoloLoQueHay`; the owner, 2026-10-08: «I want the airtight version»):
   * each product this sale takes past what is free, the shop's stock less what orders set aside,
   * needs the owner's yes, as what orders hold does, and with a connection the sale waits for the
   * server's word before its receipt, so two tills cannot both sell the last of something. It takes
   * the place of the question about what orders hold: what is free already leaves that out.
   */
  const soloLoQueHay = config.venderSoloLoQueHay;
  const faltan = soloLoQueHay
    ? faltaParaLaVenta(lineasEnBase, { pedidos: config.apartarPedidos ? pedidos : [], factorDe, hayDe, pedidoId, ubicacion })
    : [];
  const faltaPideDueno = faltan.length > 0 && !cubreLaFalta(faltan, faltaSi?.hasta ?? null);
  /** «Cobrar» asks the owner first: about what is not free, or, without that, about what orders hold. */
  const pideDueno = soloLoQueHay ? faltaPideDueno : apartadoPideDueno;
  const practica = enPractica();
  const enLinea = conRed && fase !== 'sin-conexion';
  /** Each sale waits for the server's word: on, outside practice, with a connection. Without one, it sells on, saying so. */
  const conServidor = soloLoQueHay && !practica && enLinea;
  /** So much of a product, in the unit it is counted in, as «Inventario» says it. */
  const enSuUnidad = (cantidad: Milli, productoId: string) => {
    const p = productos.find((x) => x.id === productoId);
    return p ? `${formatQuantity(cantidad)} ${etiquetaUnidad(p.unidadBase, p.unidadBase)}` : formatQuantity(cantidad);
  };
  /** Each product taken from orders, with whose orders: «Arroz 80/20: 10 libra para Pulpería La Esquina». */
  const apartados = tomaApartado.map((x) => {
    const p = productos.find((y) => y.id === x.productoId);
    const deQuienes = [...new Set(x.pedidos.map((o) => clientes.find((c) => c.id === o.clienteId)?.nombre ?? t('pedidos.clienteDesconocido')))];
    return { productoId: x.productoId, producto: p ? nombre(p) : '', cantidad: enSuUnidad(x.toma, x.productoId), clientes: deQuienes.join(', ') };
  });
  const productosApartados = apartados.map((a) => t('pos.apartadoPara', a)).join('; ');
  /** Each product past what is free: what is left, what the sale takes, and whose orders hold the rest. */
  const faltasEnVista = faltan.map((x) => {
    const p = productos.find((y) => y.id === x.productoId);
    const deQuienes = apartados.find((a) => a.productoId === x.productoId)?.clientes ?? '';
    return {
      productoId: x.productoId,
      producto: p ? nombre(p) : '',
      libre: enSuUnidad(x.libre, x.productoId),
      lleva: enSuUnidad(x.lleva, x.productoId),
      clientes: deQuienes,
      conApartado: x.apartado > 0 && deQuienes !== '',
    };
  });
  const productosQueFaltan = faltasEnVista.map((f) => t('pos.faltaPara', { producto: f.producto, libre: f.libre, lleva: f.lleva })).join('; ');
  // Each line as it is paid: so many for fewer, without the free ones.
  const subtotal = useMemo(() => carrito.reduce<number>((a, l) => a + subtotalEnCaja(l), 0) as Centavos, [carrito]);
  /** What is typed in «Envío»: none while it is empty, null while it is not an amount. */
  const envioLeido = leerEnvio(envioTexto);
  const envio = envioLeido ?? ZERO;
  /** What the sale comes to: the lines, and the delivery when there is one. Null past what the books take. */
  const conEnvio = enRango(() => addMoney(subtotal, envio));
  const totalVenta = conEnvio ?? subtotal;
  /** What is wrong with «Envío», said under it; while there is something, «Cobrar» waits. */
  const errorEnvio = envioLeido === null ? t('envio.mal') : conEnvio === null && envio > 0 ? t('envio.demasiado') : null;

  // The next sale's first line is the end of the last one's thanks.
  useEffect(() => {
    if (carrito.length > 0) setUltimoCobro(null);
  }, [carrito.length]);

  // The owner's yes was for this sale: an empty till starts without it.
  useEffect(() => {
    if (carrito.length > 0) return;
    setApartadoSi(null);
    setFaltaSi(null);
  }, [carrito.length]);

  /** The owner's yes, for what the sale takes now: more of it later asks again. */
  function autorizarApartado(usuarioId: string) {
    if (soloLoQueHay) setFaltaSi({ usuarioId, hasta: Object.fromEntries(faltan.map((x) => [x.productoId, x.lleva])) });
    else setApartadoSi({ usuarioId, hasta: Object.fromEntries(tomaApartado.map((x) => [x.productoId, x.toma])) });
  }

  /** Under a line, in red: an expired lot, and how much of it orders set aside. */
  function avisoDeLinea(productoId: string | null): string | undefined {
    if (!productoId) return undefined;
    const avisos: string[] = [];
    if (vencidos.has(productoId)) avisos.push(t('pos.loteVencido'));
    const falta = faltan.find((x) => x.productoId === productoId);
    const apartada = soloLoQueHay ? undefined : tomaApartado.find((x) => x.productoId === productoId);
    if (falta) avisos.push(t('pos.lineaQueda', { cantidad: enSuUnidad(falta.libre, productoId) }));
    if (apartada) avisos.push(t('pos.lineaApartada', { cantidad: enSuUnidad(apartada.toma, productoId) }));
    return avisos.length > 0 ? avisos.join(' · ') : undefined;
  }

  /*
   * What a customer's screen, if one is open, shows: the lines as the customer should read them,
   * in this screen's language, while there is a sale; the thanks and the change once it is
   * charged; otherwise the welcome.
   */
  /** What the offers on the sale save the customer. */
  const ahorroDelCarrito = carrito.reduce<number>((a, l) => a + ahorroEnCaja(l), 0) as Centavos;
  const pantalla: EstadoPantalla =
    carrito.length > 0
      ? {
          tipo: 'venta',
          lineas: carrito.map((l) => {
            const p = l.productoId ? productos.find((x) => x.id === l.productoId) : undefined;
            return {
              clave: l.key,
              nombre: p ? nombre(p) : l.descripcion,
              miniatura: p
                ? { nombre: p.nombre, nombreEn: p.nombreEn ?? null, categoria: p.categoria, foto: p.foto ?? null }
                : null,
              cantidad: l.cantidad,
              unidad: l.unidad ? etiquetaUnidad(l.unidad.nombre, l.unidad.nombre) : null,
              precio: l.precioUnitario,
              subtotal: subtotalEnCaja(l),
              // So many for fewer says so, and how many are free; a lower price, the list's struck.
              ...(l.lleva && l.precioUnitario === l.precioLista
                ? { lleva: { nombre: nombreDeLleva(l.lleva), gratis: gratisEnCaja(l) } }
                : ahorroEnCaja(l) > 0
                  ? { precioRegular: l.precioRegular! }
                  : {}),
            };
          }),
          ...(envio > 0 ? { envio } : {}),
          ...(ahorroDelCarrito > 0 ? { ahorro: ahorroDelCarrito } : {}),
          ...(cobrando && redondeoCobro !== 0 ? { redondeo: redondeoCobro } : {}),
          total: cobrando ? (enRango(() => addMoney(totalVenta, redondeoCobro)) ?? totalVenta) : totalVenta,
          cobrando,
        }
      : ultimoCobro
        ? { tipo: 'gracias', ...ultimoCobro }
        : { tipo: 'espera' };
  usePublicarPantalla(config.negocioNombre, pantalla);

  /*
   * What is typed at the till goes to the product search, wherever the cursor happens to be: the
   * search reads the scanner (`lectorUsb` below). A USB scanner types its code and presses Enter
   * into whatever has the focus: after a tap on a line's + that was the + (Enter pressed it again,
   * and what was scanned never rang up), after a sale it was nothing at all (the next customer's
   * first scan vanished without a word), and with the cursor left in a line's quantity it was that
   * quantity. Now a code goes to the search from anywhere, and the first key of a name typed on a
   * keyboard, with no field under the cursor, puts the cursor there.
   *
   * The receipt is the one sheet a scan goes through (`cedeAlLector`): the next customer's first
   * key ends the last one's receipt, as «Siguiente venta» would. Not with the PIN pad up, whose
   * keys those are, nor Space, which presses a focused button for somebody working by keyboard.
   */
  const campoBuscar = useRef<HTMLInputElement>(null);
  const reciboAbierto = recibo !== null;
  useEffect(() => {
    if (!reciboAbierto) return;
    function alTeclear(e: KeyboardEvent) {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1 || e.key === ' ') return;
      if (campoBuscar.current?.closest('[inert]')) return;
      if (document.querySelectorAll('div.surge, [role="dialog"]').length > 1) return;
      setRecibo(null);
    }
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [reciboAbierto]);

  /**
   * The receipt put away, by its button or its ×: the next sale, or back to the orders when it
   * delivered one sent from there. The next customer's first key (above) always stays at the till.
   */
  function guardarRecibo() {
    const aPedidos = recibo?.aPedidos === true;
    setRecibo(null);
    if (aPedidos) void irA({ to: '/pedidos' });
  }

  function costoDe(productoId: string): UnitCost {
    return costoConocido(productoId, ubicacion, existencias, compras);
  }

  /*
   * «Cobrar» on the list of orders comes here with the order (`?pedido=`, the owner, 2026-10-04).
   * Loaded once, and the address cleared, so a reload or a «Volver» from «Historial» does not load
   * it again over whatever the sale has become. Read straight from the books, not from this
   * screen's lists, which may not have filled yet on arrival. Not into somebody else's till, and
   * not with none open: «Empezar el día» comes first and goes back here, to the till still waiting
   * with the order in its address. One already delivered, or cancelled, is not loaded, and a sheet
   * says why.
   */
  const { pedido: pedidoBuscado } = useSearch({ from: '/pos' });
  const pedidoPorCargar = useLiveQuery(
    async () => (pedidoBuscado ? ((await db.pedidos.get(pedidoBuscado)) ?? null) : null),
    [pedidoBuscado],
    null,
  );
  const atendido = useRef<string | null>(null);
  const [noSeCarga, setNoSeCarga] = useState<string | null>(null);
  /**
   * An order «Cobrar» sent while a sale was already being rung up here: asked about before the
   * order takes the sale's place, since whoever pressed it came from another screen and may not
   * know the cart held anything. Said no to, the sale stays and the order stays to deliver.
   */
  const [porReemplazar, setPorReemplazar] = useState<{
    pedido: Pedido;
    datos: Parameters<typeof lineasDelPedido>[1];
  } | null>(null);
  useEffect(() => {
    if (!pedidoBuscado || !caja || cajaDeOtro !== null || atendido.current === pedidoBuscado) return;
    atendido.current = pedidoBuscado;
    const dondeVende = caja.ubicacion;
    void (async () => {
      const p = await db.pedidos.get(pedidoBuscado);
      if (p?.estado === 'pendiente') {
        const [productos, unidades, precios, existencias, compras] = await Promise.all([
          db.productos.toArray(),
          db.unidades.toArray(),
          db.precios.toArray(),
          db.existencias.toArray(),
          db.compras.toArray(),
        ]);
        const datos = { productos, unidades, precios, costoDe: (id: string) => costoConocido(id, dondeVende, existencias, compras) };
        // The same order already in the cart is left as it is, with whatever was changed in it.
        if (pedidoId === p.id) {
          // Nothing to load.
        } else if (carrito.length > 0) setPorReemplazar({ pedido: p, datos });
        else cargarPedido(p, datos, true);
      } else {
        const cliente = p ? ((await db.clientes.get(p.clienteId))?.nombre ?? t('pos.unCliente')) : '';
        const venta = p?.ventaId ? await db.ventas.get(p.ventaId) : undefined;
        setNoSeCarga(
          !p
            ? t('pedidos.noEsta')
            : p.estado === 'entregado'
              ? t('pedidos.yaEntregado', { cliente, ticket: venta?.ticket ?? '—' })
              : t('pedidos.fueCancelado', { cliente, motivo: p.motivoCancelacion ?? '—' }),
        );
      }
      void navigate({ to: '/pos', search: {}, replace: true });
    })();
    // `cargarPedido`, `t` and the cart are this render's; what decides is the order, the till and whose it is.
  }, [pedidoBuscado, caja, cajaDeOtro]);

  /**
   * Adding a product that has no price on the current list.
   *
   * It used to go in at C$0.00, which the reducer accepts and the ledger books as a loss equal
   * to the cost of the goods — the most ordinary mistake there is, giving stock away silently.
   * Now the line is added unpriced, the editor opens on it straight away, and `cobrar` refuses
   * while anything is still at zero.
   */
  function agregar(producto: Producto, elegida?: UnidadVenta) {
    const us = unidadesDe(unidades, producto.id);
    // A presentation's own barcode rings up that presentation: the box, not one can.
    const unidad: UnidadVenta | null = us.find((u) => u.id === elegida?.id) ?? us[0] ?? null;
    // An offer running on the list is the price (`lib/ofertas.ts`).
    const cobra = unidad ? precioEnCaja(precios, ofertas, unidad, listaId, hoy()) : null;
    const precio = cobra?.precio ?? null;
    const clave = newId();
    if (precio === null) {
      // Whoever may not change prices cannot type one either: the owner sets it, or the line goes.
      setAviso(t(puedeEditarPrecio ? (puedePrecios ? 'pos.faltaPrecio' : 'pos.faltaPrecioSoloAqui') : 'pos.faltaPrecioPida', { producto: nombre(producto) }));
      setEditando(clave);
    }

    setCarrito((c) => {
      // Same product and unit again: bump the quantity rather than adding a second line.
      const existente = c.find((l) => l.productoId === producto.id && l.unidad?.id === unidad?.id);
      if (existente) {
        return c.map((l) =>
          l.key === existente.key ? { ...l, cantidad: (l.cantidad + fromUnits(1)) as Milli } : l,
        );
      }
      return [
        ...c,
        {
          key: clave,
          productoId: producto.id,
          descripcion: producto.nombre,
          unidad,
          cantidad: fromUnits(1),
          precioLista: precio ?? ZERO,
          ...(listaId ? { listaId } : {}),
          precioUnitario: precio ?? ZERO,
          motivoOverride: null,
          autorizadoPor: null,
          costoReferencia: costoDe(producto.id),
          ...deLaOferta(cobra),
        },
      ];
    });
  }

  /**
   * A line's offer, as its list price now says: the one running whose price that is for its
   * presentation, or none. For a line the sheet put in another presentation.
   */
  function conSuOferta(l: LineaCarrito): LineaCarrito {
    const sin = sinOferta(l);
    if (!l.unidad || !l.productoId) return sin;
    const cobra = precioEnCaja(precios, ofertas, l.unidad, l.listaId ?? listaId, hoy());
    return cobra?.ofertaId && cobra.precio === l.precioLista ? { ...sin, ...deLaOferta(cobra) } : sin;
  }

  /**
   * The sale with each line whose offer has gone (its last day gone by, the sale kept overnight, or
   * ended meanwhile by the owner) at the list's price: a price typed by hand stays, the list's under
   * it now the list's own. Null when none has. What the till charges is what runs; an offer that
   * starts once a line is in the sale is not put on it (an order's price, say, was agreed).
   */
  function sinOfertasTerminadas(c: readonly LineaCarrito[]): LineaCarrito[] | null {
    if (!ofertasLeidas) return null;
    const dia = hoy();
    let cambio = false;
    const nuevo = c.map((l) => {
      if (!l.ofertaId || !l.unidad) return l;
      const cobra = precioEnCaja(precios, ofertas, l.unidad, l.listaId ?? listaId, dia);
      // Nothing to charge instead, the prices not read yet: it waits.
      if (cobra === null || cobra.ofertaId === l.ofertaId) return l;
      cambio = true;
      const aMano = l.precioUnitario !== l.precioLista;
      return { ...sinOferta(l), precioLista: cobra.precio, precioUnitario: aMano ? l.precioUnitario : cobra.precio, ...deLaOferta(cobra) };
    });
    return cambio ? nuevo : null;
  }

  // As soon as an offer on the sale has gone, its lines show the list's price.
  useEffect(() => {
    const alDia = sinOfertasTerminadas(carrito);
    if (alDia) setCarrito(alDia);
    // What decides is the sale, the offers and the prices; the rest is this render's.
  }, [carrito, ofertasLeidas, precios]);

  /**
   * Charges the whole sale at another list: what is already in the cart moves with it, each line's
   * own list too, and what is added next comes in at it.
   *
   * Switching used to change only what was added afterwards, so choosing a wholesale client
   * after ringing up their rice left the rice at the retail price, and nothing on screen said
   * so. A price somebody set by hand stays where they set it, because that was a decision and
   * not the list's; so does a line whose product has no price on the new list, rather than
   * dropping to zero and blocking the sale. Those keep the list their price came from.
   *
   * The owner's yes to another list stays with the sale back at its own: the sale records it only
   * if a list that needed it is used (`cobrar`).
   */
  function cambiarLista(id: string) {
    const antes = listaId;
    setListaId(id);
    setCarrito((c) =>
      c.map((l) => {
        if (!l.unidad) return l;
        const cobra = l.precioUnitario === l.precioLista ? precioEnCaja(precios, ofertas, l.unidad, id, hoy()) : null;
        if (cobra === null) return l.listaId || !antes ? l : { ...l, listaId: antes };
        return { ...sinOferta(l), listaId: id, precioLista: cobra.precio, precioUnitario: cobra.precio, ...deLaOferta(cobra) };
      }),
    );
  }

  /**
   * One line at another list, from the row at the foot of its card (the owner, 2026-10-11: «choose
   * for each item on the screen»): the case of oil at wholesale, the rest of the sale at retail. The
   * line takes the list's price, a price typed by hand included: picking a list on that very line
   * is the decision this time, and the reason for the old price goes with it.
   */
  function cambiarListaDeLinea(key: string, id: string) {
    setCarrito((c) =>
      c.map((l) => {
        if (l.key !== key || !l.unidad) return l;
        const cobra = precioEnCaja(precios, ofertas, l.unidad, id, hoy());
        return cobra === null
          ? l
          : { ...sinOferta(l), listaId: id, precioLista: cobra.precio, precioUnitario: cobra.precio, motivoOverride: null, ...deLaOferta(cobra) };
      }),
    );
  }

  /**
   * One line in another of its product's presentations, from the row at the foot of its card (the
   * owner, 2026-10-18): the box of 12 Oreos at the box's price, not 12 packets typed with the box's
   * count kept in the cashier's head. As many as it had, of the new one, at its price on the line's
   * list. A price typed by hand goes with its reason, and so does any yes the owner gave the line:
   * that was for other goods. With no price there it moves nothing.
   */
  function cambiarPresentacionDeLinea(key: string, unidadVentaId: string) {
    const unidad = unidades.find((u) => u.id === unidadVentaId);
    if (!unidad) return;
    setCarrito((c) =>
      c.map((l) => {
        if (l.key !== key || l.productoId !== unidad.productoId) return l;
        const cobra = precioEnCaja(precios, ofertas, unidad, l.listaId ?? listaId, hoy());
        if (cobra === null) return l;
        const { autorizadoEnElPedido: _delPedido, ...suya } = sinOferta(l);
        return { ...suya, unidad, precioLista: cobra.precio, precioUnitario: cobra.precio, motivoOverride: null, autorizadoPor: null, ...deLaOferta(cobra) };
      }),
    );
  }

  /**
   * Picking a client switches the price list to theirs.
   *
   * This is requirement 5 made mechanical: the tier a customer was promised is attached to the
   * customer, so nobody has to remember who gets mayorista and the cashier cannot forget.
   */
  function elegirCliente(id: string | null) {
    setClienteId(id);
    // The order being delivered stays the sale's while it is its own client — picked again to put
    // it on fiado, say: unlinked, the sale went through and the order stayed pending, to be
    // delivered a second time (2026-10-13). Another client's sale is not that order's.
    const pedido = pedidoId ? pedidos.find((p) => p.id === pedidoId) : undefined;
    const sigue = pedido !== undefined && pedido.clienteId === id;
    if (pedidoId && !sigue) setPedidoId(null);
    const c = clientes.find((x) => x.id === id);
    // Its order's list while it is still that order (`listaPropia`), theirs otherwise.
    const lista = (sigue ? pedido.listaId : null) ?? c?.listaId ?? listaDeLaTienda;
    if (lista) cambiarLista(lista);
    // Their own list now; another one is asked for again.
    setListaAutorizadaPor(null);
    setListasAutorizadas([]);
    setEligiendoCliente(false);
    setClienteNuevo(null);
    setBuscaCliente('');
    if (fiarTrasElegir) {
      setFiarTrasElegir(false);
      if (id) setCobrando(true);
      // Nobody to fiar: the next «Cobrar» starts on cash again.
      else setFiadoPedido(false);
    }
  }

  /**
   * Somebody new at the counter, added without leaving the sale: saved, put on the sale, and —
   * when fiado is what sent them here — straight back to paying on fiado. They start with no
   * limit unless the owner set one on the spot, so a cashier giving them fiado still needs the
   * owner's PIN, which is the point.
   */
  async function crearCliente(b: BorradorCliente): Promise<string | null> {
    const id = newId();
    const { rechazo } = await emitirYVer('cliente.creado', {
      clienteId: id,
      ...camposDe(b, { limite: puedeDarCredito, lista: puedeElegirLista, cedula: true }),
    });
    if (rechazo) return rechazo;
    elegirCliente(id);
    return null;
  }

  /**
   * The list radio. The sale's own list, or anything for the owner, goes straight through;
   * any other list, from a cashier, asks for the owner's PIN first, unless the owner already said
   * yes to it on this sale.
   */
  function pedirLista(id: string) {
    if (id === listaId) return;
    if (sinPedirla(id)) cambiarLista(id);
    else setPidiendoLista({ listaId: id });
  }

  /** A line's list, from its card: asked for as the sale's is. */
  function pedirListaDeLinea(key: string, id: string) {
    if (sinPedirla(id)) cambiarListaDeLinea(key, id);
    else setPidiendoLista({ listaId: id, key });
  }

  /**
   * A line's price lists, at the foot of its card: when the shop has more than one, and a list other
   * than the line's own has a price for what the line sells. A quick sale has none.
   */
  function listasDeLinea(l: LineaCarrito): ListasDeLinea | undefined {
    if (activas.length < 2 || !l.productoId || !l.unidad) return undefined;
    const unidadId = l.unidad.id;
    const elegida = l.listaId ?? listaId;
    const opciones = activas.map((x) => ({
      id: x.id,
      nombre: etiquetaCortaLista(x),
      conPrecio: precioDe(precios, unidadId, x.id) !== null,
    }));
    if (!opciones.some((o) => o.conPrecio && o.id !== elegida)) return undefined;
    return { opciones, elegida, onElegir: (id) => pedirListaDeLinea(l.key, id) };
  }

  /**
   * A line's presentations, over its lists: when its product is sold in more than one, and another
   * than the line's has a price on the line's list. For whoever sells: the box or the packet is what
   * the customer asked for, not a price to be allowed. A quick sale has none.
   */
  function presentacionesDeLinea(l: LineaCarrito, producto: Producto | undefined): PresentacionesDeLinea | undefined {
    if (!producto || !l.unidad) return undefined;
    const us = unidadesDe(unidades, producto.id);
    if (us.length < 2) return undefined;
    const lista = l.listaId ?? listaId;
    const elegida = l.unidad.id;
    const opciones = us.map((u) => ({
      id: u.id,
      nombre: etiquetaPresentacion(u, producto.unidadBase),
      conPrecio: precioDe(precios, u.id, lista) !== null,
    }));
    if (!opciones.some((o) => o.conPrecio && o.id !== elegida)) return undefined;
    return { opciones, elegida, onElegir: (id) => cambiarPresentacionDeLinea(l.key, id) };
  }

  /**
   * Loads an order into the cart at the prices the customer was quoted when it was taken. Orders
   * only come here from «Cobrar» on their list, so that is where the till goes back once it is
   * charged.
   */
  /**
   * `desdePedidos`: sent here by «Cobrar» on another screen, which the receipt goes back to. One
   * picked here, among the client's, stays here after its sale: it used to send the till to
   * «Pedidos» (2026-10-13).
   */
  function cargarPedido(
    p: Pedido,
    datos: Parameters<typeof lineasDelPedido>[1] = { productos, unidades, precios, costoDe },
    desdePedidos = false,
  ) {
    setClienteId(p.clienteId);
    setPedidoId(p.id);
    setDesdePedidos(desdePedidos);
    setListaId(p.listaId);
    setListaAutorizadaPor(null);
    setListasAutorizadas([]);
    setCarrito(lineasDelPedido(p, datos, t('pos.producto')));
    // What the order said the trip costs, there to change before «Cobrar».
    setEnvioTexto(textoDeEnvio(p.envio));
    setEligiendoCliente(false);
  }

  function aplicar(key: string, cambios: Partial<LineaEditable>, autorizadoPor?: string) {
    setCarrito((c) =>
      c.map((l) => {
        if (l.key !== key) return l;
        const cambiada = { ...l, ...cambios };
        // In another presentation, at its price on the line's list: the offer, if any, is that one's.
        const nueva = cambiada.unidad?.id === l.unidad?.id ? cambiada : conSuOferta(cambiada);
        const sigue = rapidaQueSube(l, nueva) ? null : l.autorizadoPor;
        return { ...nueva, autorizadoPor: autorizadoPor ?? sigue };
      }),
    );
    setEditando(null);
    setAviso(null);
  }

  /**
   * How many, from the line's own − and + or its number: the change made all day, a second soda
   * or one bag fewer, without opening the editor. Never down to nothing: taking a line off is its
   * bin's job, and the bin asks.
   *
   * A price the owner authorised below the margin stays authorised at any quantity. The PIN
   * answers «¿a este precio?», and the sale records who gave it either way.
   */
  function cambiarCantidad(key: string, cambio: (actual: Milli) => Milli) {
    setCarrito((c) =>
      c.map((l) => {
        if (l.key !== key) return l;
        const cantidad = cambio(l.cantidad);
        if (cantidad <= 0) return l;
        const nueva = { ...l, cantidad };
        return rapidaQueSube(l, nueva) ? { ...nueva, autorizadoPor: null } : nueva;
      }),
    );
  }

  /**
   * Takes a line off the sale, once «¿Quitar de la venta?» has been answered.
   *
   * Both ways in ask first: the bin beside the line, and «Quitar» in its editor. A bin beside
   * every line is easy to press by accident at a busy counter, and so is «Quitar» sitting next to
   * «Guardar». Scanning it again brings the product back, but not what may have been done to the
   * line: a price somebody typed, its reason, the owner's PIN.
   */
  function quitar(key: string) {
    setCarrito((c) => c.filter((l) => l.key !== key));
    setEditando(null);
    // Its last line gone, the sale is no longer that order: a refused one (cancelled meanwhile)
    // would otherwise refuse every sale after it for the same reason. Nor is its delivery anybody's:
    // left there, it would go on the next customer's bill.
    if (carrito.every((l) => l.key === key)) {
      setPedidoId(null);
      setEnvioTexto('');
    }
  }

  async function cobrar(datos: {
    pagos: PagoBorrador[];
    redondeo: Centavos;
    total: Centavos;
    cambio: Centavos;
    envio: Centavos;
  }) {
    if (!caja || !sesion) {
      setCobrando(false);
      return;
    }
    setError(null);
    // An offer gone since its line went in, the till left open overnight: back to the list's price,
    // shown before it is charged.
    const alDia = sinOfertasTerminadas(carrito);
    if (alDia) {
      const terminada = carrito.find((l, i) => l.ofertaId && alDia[i]?.ofertaId !== l.ofertaId);
      const producto = terminada?.productoId ? productos.find((p) => p.id === terminada.productoId) : undefined;
      setCarrito(alDia);
      setCobrando(false);
      setError(t('pos.ofertaTermino', { producto: producto ? nombre(producto) : (terminada?.descripcion ?? '') }));
      return;
    }

    try {
      const ticket = await siguienteTicket();
      const ventaId = newId();

      const lineas = carrito.flatMap((l) => {
        const linea = {
          productoId: l.productoId,
          descripcion: l.descripcion,
          unidadVentaId: l.unidad?.id ?? null,
          cantidad: l.cantidad,
          factor: l.unidad?.factor ?? fromUnits(1),
          precioLista: l.precioLista,
          // Only a line at another list than the sale's says which; the rest read the sale's.
          ...(l.productoId && l.listaId && l.listaId !== listaId ? { listaId: l.listaId } : {}),
          precioUnitario: l.precioUnitario,
          ...(l.motivoOverride ? { motivoOverride: l.motivoOverride } : {}),
          ...(l.autorizadoPor ? { autorizadoPor: l.autorizadoPor } : {}),
          // The offer it was charged at, and the list's own price: what the offer gave, and «Por comprar» leaves out.
          ...(l.productoId && l.ofertaId ? { ofertaId: l.ofertaId, ...(l.precioRegular !== undefined ? { precioRegular: l.precioRegular } : {}) } : {}),
          costoReferencia: l.costoReferencia,
          subtotal: lineTotal(l.precioUnitario, l.cantidad),
        };
        // So many for fewer (2026-11-03): what is paid at its price, and the free ones as a line of their
        // own at nothing, named for the offer, so that every line is its quantity by its price.
        const gratis = l.productoId && l.lleva ? gratisEnCaja(l) : 0;
        if (gratis <= 0 || !l.lleva) return [linea];
        const pagadas = (l.cantidad - gratis) as Milli;
        return [
          { ...linea, cantidad: pagadas, subtotal: lineTotal(l.precioUnitario, pagadas) },
          { ...linea, descripcion: `${l.descripcion} (${nombreDeLleva(l.lleva)})`, cantidad: gratis, precioLista: ZERO, precioUnitario: ZERO, subtotal: ZERO },
        ];
      });
      /** The sale, or a line of it, at a list that is not its own: then, and only then, the owner's yes goes on it. */
      const otraLista = listaId !== listaPropia || lineas.some((l) => l.listaId !== undefined && l.listaId !== listaPropia);

      const venta = {
        ventaId,
        ticket,
        sesionId: caja.id,
        fecha: hoy(),
        ubicacion,
        listaId,
        ...(listaAutorizadaPor && otraLista ? { listaAutorizadaPor } : {}),
        // Only when it takes from what orders set aside, and only the owner who said yes to it.
        ...(!soloLoQueHay && tomaApartado.length > 0 && apartadoSi ? { apartadoAutorizadoPor: apartadoSi.usuarioId } : {}),
        ...(faltan.length > 0 && faltaSi ? { faltaAutorizadaPor: faltaSi.usuarioId } : {}),
        // The server is asked to refuse it if what it takes has gone meanwhile, to another till.
        ...(conServidor ? { soloSiHay: true as const } : {}),
        clienteId,
        pedidoId,
        lineas,
        descuento: 0,
        // Only when there is one: a phone not yet updated reads a sale carrying it as not adding up.
        ...(datos.envio > 0 ? { envio: datos.envio } : {}),
        redondeo: datos.redondeo,
        total: datos.total,
        pagos: datos.pagos,
        cambio: datos.cambio,
      };
      const { id, rechazo } = await emitirYVer('venta.registrada', venta);

      const guardada = await db.ventas.get(ventaId);
      // Refused here, it never happened: the cart stays for another try, and the screen says why.
      if (rechazo || !guardada) {
        setError(rechazo ?? t('pos.noSePudoRegistrar'));
        setCobrando(false);
        return;
      }
      /*
       * The server's word before the receipt. Refused, another till sold it first: nothing was
       * charged, the cart stays, and the line now says what is left. No word in time, the customer is
       * not kept waiting: a copy goes behind it that stands whatever the stock, and is nothing if the
       * sale stood (`respaldo`), and the receipt says it went unconfirmed.
       */
      let sinConfirmar = false;
      if (conServidor) {
        setConfirmando(true);
        const respuesta = await esperarRespuesta(id, ESPERA_VENTA_MS);
        setConfirmando(false);
        if (respuesta.estado === 'rechazado') {
          setError(t('pos.yaNoHay', { motivo: respuesta.motivo }));
          setCobrando(false);
          return;
        }
        if (respuesta.estado === 'sin-respuesta') {
          const { soloSiHay: _pregunta, ...sinPregunta } = venta;
          await emitir('venta.registrada', { ...sinPregunta, respaldo: true });
          sinConfirmar = true;
        }
      }
      // Read back after the sale, so a fiado receipt says what they owe with this sale in it.
      const quien = clienteId ? await db.clientes.get(clienteId) : undefined;
      setRecibo({
        texto: textoRecibo(guardada, datosNegocio(config), {
          ...(quien ? { cliente: { nombre: quien.nombre, saldo: quien.saldo ?? 0 } } : {}),
          presentaciones: new Map(unidades.map((u) => [u.id, u.nombre])),
        }),
        ticket,
        ...(quien?.telefono ? { telefono: quien.telefono } : {}),
        ...(sinConfirmar ? { sinConfirmar } : {}),
        ...(pedidoId && desdePedidos ? { aPedidos: true } : {}),
      });

      // What the offers saved, for the customer's screen to thank them with.
      const ahorro = ahorroDelCarrito;
      setUltimoCobro({ total: datos.total, cambio: datos.cambio, ...(ahorro > 0 ? { ahorro } : {}) });
      setCarrito([]);
      setCobrando(false);
      setFiadoPedido(false);
      setClienteId(null);
      setPedidoId(null);
      setEnvioTexto('');
      // The next customer starts at the shop's list, not at whatever the last one got.
      setListaId(listaDeLaTienda);
      setListaAutorizadaPor(null);
      setListasAutorizadas([]);
      setApartadoSi(null);
      setFaltaSi(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('pos.noSePudoRegistrar'));
      setConfirmando(false);
      setCobrando(false);
    }
  }

  // --- Sin caja abierta ---
  if (caja === undefined) {
    return (
      <div className={`${entrada} vt-pagina mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-5 p-6 text-center`}>
        <span className="chip chip-ambar size-16 rounded-2xl">
          <Ico icono="caja" className="size-8" />
        </span>
        <h1 className="text-2xl font-bold">{t('pos.sinCaja')}</h1>
        <p className="text-sm text-slate-400">{t('pos.sinCajaExplicacion')}</p>
        {pedidoPorCargar?.estado === 'pendiente' && (
          <p className="rounded-xl bg-teal-950/60 px-4 py-3 text-sm text-teal-200 ring-1 ring-teal-900 ring-inset" data-pedido-espera>
            {t('pos.pedidoDespues', {
              cliente: clientes.find((c) => c.id === pedidoPorCargar.clienteId)?.nombre ?? t('pos.unCliente'),
            })}
          </p>
        )}
        <Link to="/caja/empezar" search={pedidoBuscado ? { pedido: pedidoBuscado } : {}} className="btn-primario">
          <Ico icono="caja" />
          {t(empiezaElDia !== false ? 'turno.empezarDia' : 'turno.empezarTurno')}
        </Link>
        {/* Back the way it came: to the orders, when «Cobrar» brought it here. */}
        <BotonVolver a="/" className="btn-texto gap-1.5 text-sm text-slate-400" />
      </div>
    );
  }

  // --- La caja es de otra persona ---
  if (cajaDeOtro) {
    const nombre = cajaDeOtro.nombre;
    return (
      <div className={`${entrada} vt-pagina mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-5 p-6 text-center`}>
        <span className="chip chip-ambar size-16 rounded-2xl">
          <Ico icono="caja" className="size-8" />
        </span>
        <h1 className="text-2xl font-bold">{t('entrega.cajaDe', { nombre })}</h1>
        <p className="text-sm text-slate-400">{t('entrega.paraVender', { nombre })}</p>
        {pedidoPorCargar?.estado === 'pendiente' && (
          <p className="rounded-xl bg-teal-950/60 px-4 py-3 text-sm text-teal-200 ring-1 ring-teal-900 ring-inset" data-pedido-espera>
            {t('pos.pedidoAlRecibir', {
              cliente: clientes.find((c) => c.id === pedidoPorCargar.clienteId)?.nombre ?? t('pos.unCliente'),
            })}
          </p>
        )}
        <Link to="/caja/recibir" search={pedidoBuscado ? { pedido: pedidoBuscado } : {}} className="btn-primario">
          <Ico icono="contar" />
          {t('entrega.recibirDe', { nombre })}
        </Link>
        <BotonVolver a="/" className="btn-texto gap-1.5 text-sm text-slate-400" />
      </div>
    );
  }

  const nombreLista = (id: string | null | undefined): string | null => {
    const lista = listas.find((l) => l.id === id);
    return lista ? etiquetaListaPrecio(lista) : null;
  };

  const enEdicion = carrito.find((l) => l.key === editando);
  const productoEnEdicion = enEdicion?.productoId
    ? productos.find((p) => p.id === enEdicion.productoId)
    : undefined;
  const aQuitar = carrito.find((l) => l.key === porQuitar);
  const productoAQuitar = aQuitar?.productoId
    ? productos.find((p) => p.id === aQuitar.productoId)
    : undefined;

  return (
    <div className={`${entrada} vt-pagina mx-auto flex min-h-dvh w-full max-w-2xl flex-col`}>
      {/*
        An app bar that never wraps: back where people look for it, the screen's name, the sales
        so far and the till. The client used to share this row with them, and on a 320px phone
        «Volver» ended up alone on a second line.

        When the row runs short, the name is what gives way: the buttons are what it is for, and
        nobody on this screen needs telling it is «Vender». Below 400px the name is left to screen
        readers, and below 360px «Historial» keeps only its icon.

        Leaving the till is going back to where you came from, so it says «Volver» with the same
        arrow as every other screen, and goes there: home, «Caja», the orders (`lib/volver.ts`).
        It used to say «Salir», which came out in English as "Leave", a word for abandoning
        something, on the one button somebody presses forty times a day to get back to the home
        screen.
      */}
      <header className="flex items-center gap-2 border-b border-slate-800 px-3 py-2">
        <BotonVolver a="/" className="btn-secundario h-11 min-h-0 shrink-0 gap-1.5 pr-3 pl-2 text-sm" />
        <h1 className="min-w-0 flex-1 truncate text-center text-base font-semibold max-[399px]:sr-only">
          {t('inicio.vender')}
        </h1>
        {/*
          Only where a second monitor can hang off the till: a laptop or a desktop, with a mouse.
          A phone has nowhere to put the window, and on one this row is already full.
        */}
        <button
          type="button"
          className="btn-secundario hidden h-11 min-h-0 shrink-0 gap-1.5 px-3 text-sm md:pointer-fine:inline-flex"
          onClick={abrirPantallaCliente}
        >
          <Ico icono="pantalla" className="size-4" />
          {t('pos.pantallaCliente')}
        </button>
        <Link
          to="/ventas"
          search={{ desde: 'pos' }}
          className="btn-secundario ml-auto h-11 min-h-0 min-w-11 shrink-0 gap-1.5 px-3 text-sm"
        >
          <Ico icono="ventas" className="size-4" />
          <span className="max-[359px]:sr-only">{t('pos.historial')}</span>
        </Link>
        <Link to="/caja" className="btn-secundario h-11 min-h-0 shrink-0 gap-1.5 px-3 text-sm">
          <Ico icono="caja" className="size-4" />
          {t('pos.caja')}
        </Link>
      </header>

      {/*
        Who this sale is for, and so at what price.

        The client is a whole row that says who is picked, «Cliente de mostrador» until somebody
        is, because a button that only ever read «Cliente» said neither what it was for nor what
        it was set to. The price list sits with it: picking a client moves it, so the two are
        read together.

        The price list used to be a dropdown reading «Público», which hid the other list behind
        a tap and never said it was a price at all.
      */}
      <section className="flex flex-col gap-3 border-b border-slate-800 px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="tarjeta-accion flex min-w-0 flex-1 items-center gap-3 px-4 py-2.5 text-left"
            onClick={() => setEligiendoCliente(true)}
            data-guia="pos-cliente"
            data-con-cliente={cliente ? '' : undefined}
          >
            <Ico icono="clientes" className="size-5 shrink-0 text-slate-400" />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">
                {cliente ? cliente.nombre : t('pos.clienteMostrador')}
              </span>
              {/*
                What they already owe, under their name, where whoever is about to give them more
                fiado can see it. Beside the name it took the room the name needed: at 320 px the
                name got 5 px, and it was cut at every phone width.
              */}
              {cliente && (cliente.saldo ?? 0) > 0 ? (
                <span className="mt-0.5 flex min-w-0 items-center gap-2 text-xs leading-snug">
                  <span className="pastilla tabular shrink-0 bg-amber-500/15 text-amber-200">
                    {t('fiado.debe', { monto: formatMoney(cliente.saldo!) })}
                  </span>
                  <span className="truncate text-slate-400">{t('pos.clienteCambiar')}</span>
                </span>
              ) : (
                <span className="block text-xs leading-snug text-slate-400">
                  {t(cliente ? 'pos.clienteCambiar' : 'pos.clienteAyuda')}
                </span>
              )}
            </span>
            <Ico icono="adelante" className="size-4 shrink-0 text-slate-500" />
          </button>
          {cliente && (
            <button
              type="button"
              className="btn-secundario size-11 min-h-0 shrink-0 p-0"
              aria-label={t('pos.quitarCliente')}
              onClick={() => elegirCliente(null)}
            >
              <Ico icono="cerrar" className="size-4" />
            </button>
          )}
        </div>

        {/*
          Somebody who owes pays some of it at the counter, often while buying something else. It
          was on the client's own page only: the till, home, «Todo lo demás», «Clientes», the
          search, the client, «Abonar», and all the way back, thirteen taps across eight screens,
          with the sale left behind. Its own row, under the client's: beside the name it would
          take the room the name needs on a narrow phone.
        */}
        {cliente && (cliente.saldo ?? 0) > 0 && (
          <button
            type="button"
            className="btn-secundario h-11 min-h-0 text-sm"
            onClick={() => setAbonando(true)}
            data-guia="pos-abonar"
          >
            <Ico icono="cobrar" className="size-4" />
            {t('pos.abonar')}
          </button>
        )}

        {activas.length > 0 && (
          <div role="radiogroup" aria-labelledby="pos-cobrar-a">
            <p id="pos-cobrar-a" className="mb-1.5 text-xs text-slate-400">
              {t('pos.cobrarA')}
            </p>
            {/* Side by side where they fit, one under the other on a narrow phone. */}
            <div className="flex flex-wrap gap-2">
              {activas.map((l) => {
                const elegida = l.id === listaId;
                return (
                  <label
                    key={l.id}
                    className={`flex min-h-11 flex-1 basis-36 cursor-pointer items-center gap-2 rounded-xl px-3 text-sm font-medium ring-1 ring-inset transition ${
                      elegida ? 'bg-teal-500/15 text-teal-100 ring-teal-500/60' : 'text-slate-300 ring-slate-700 hover:bg-white/5 hover:text-slate-100 hover:ring-slate-500'
                    }`}
                  >
                    <input
                      type="radio"
                      name="pos-lista"
                      value={l.id}
                      checked={elegida}
                      onChange={() => pedirLista(l.id)}
                      className="size-4 shrink-0 accent-teal-500"
                    />
                    {etiquetaListaPrecio(l)}
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {pedidoId && (
        <p className="border-b border-teal-900 bg-teal-950/60 px-4 py-2 text-xs text-teal-300">
          {t('pos.entregandoPedido', { cliente: cliente?.nombre ?? t('pos.unCliente') })}
        </p>
      )}

      <div className="border-b border-slate-800 px-4 py-3" data-guia="pos-buscar">
        {/*
          The camera stays open for the whole sale: one can after another, each one a beep and a
          line, with the total climbing beside «Listo». A code the shop has never seen can be
          taught to its product right there, or sold as a venta rápida.
        */}
        <BuscadorProducto
          onElegir={agregar}
          etiqueta=""
          autoFocus
          campoRef={campoBuscar}
          // A code read wherever the cursor was, and one nobody has a question that stops the sale
          // until it is answered: a can that did not ring up was a can given away.
          lectorUsb
          preguntarEnHoja
          // A code on two products asks which with each one's price on this sale.
          listaId={listaId}
          escaner={{
            continuo: true,
            titulo: t('inicio.vender'),
            describir: (p, unidad) => {
              // The line `agregar` just rang up: the presentation read, or the product's first. It
              // took the product's first line whatever was read, and a bottle scanned after a case
              // showed the case's price.
              const us = unidadesDe(unidades, p.id);
              const u = us.find((x) => x.id === unidad?.id) ?? us[0];
              const linea = carrito.find((l) => l.productoId === p.id && l.unidad?.id === u?.id);
              if (!linea) return null;
              // Which presentation, when there is more than one: the case, not the can.
              return (
                <LineaEscaneada cantidad={linea.cantidad} precio={linea.precioUnitario} unidad={us.length > 1 ? linea.unidad : null} />
              );
            },
            resumen: carrito.length > 0 && (
              <div>
                <p className="text-xs text-slate-400">{t('pos.enLaVenta', { n: carrito.length })}</p>
                <p key={totalVenta} className="tabular late text-2xl font-bold tracking-tight">
                  {formatMoney(totalVenta)}
                </p>
              </div>
            ),
            desconocido: (_codigo, cerrar) => (
              <button
                type="button"
                className="btn-secundario"
                onClick={() => {
                  cerrar();
                  setRapida(true);
                }}
              >
                <Ico icono="rapido" />
                {t('pos.ventaRapida')}
              </button>
            ),
          }}
        />
        <button
          type="button"
          className="btn-texto mt-2 gap-1.5 text-xs text-slate-500"
          onClick={() => setRapida(true)}
        >
          <Ico icono="rapido" className="size-3.5" />
          {t('pos.ventaRapidaEnlace')}
        </button>
      </div>

      {/* It is about an unpriced line, so it goes once none is left: priced, or taken off. */}
      {aviso && sinPrecio > 0 && (
        <p className="border-b border-amber-900 bg-amber-950/60 px-4 py-2 text-xs text-amber-200">
          {aviso}
        </p>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-3">
        {carrito.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-16 text-center">
            <span className="chip chip-neutro size-16 rounded-2xl">
              <Ico icono="vender" className="size-8" />
            </span>
            <p className="text-sm text-slate-500">{t('pos.busqueEmpezar')}</p>
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {carrito.map((l) => {
              const producto = l.productoId
                ? productos.find((p) => p.id === l.productoId)
                : undefined;
              const nombreLinea = producto ? nombre(producto) : l.descripcion;
              return (
                <LineaDeCarrito
                  key={l.key}
                  data-guia="pos-linea"
                  producto={producto}
                  nombre={nombreLinea}
                  cantidad={l.cantidad}
                  onCambiarCantidad={(cambio) => cambiarCantidad(l.key, cambio)}
                  subtotal={subtotalEnCaja(l)}
                  precio={l.precioUnitario}
                  precioLista={l.precioLista}
                  precioRegular={l.ofertaId && !l.lleva ? (l.precioRegular ?? null) : undefined}
                  lleva={l.lleva && l.precioUnitario === l.precioLista ? { nombre: nombreDeLleva(l.lleva), cantidad: l.lleva.cantidad, gratis: gratisEnCaja(l) } : undefined}
                  unidad={l.unidad}
                  aviso={avisoDeLinea(l.productoId)}
                  presentaciones={presentacionesDeLinea(l, producto)}
                  listas={listasDeLinea(l)}
                  etiquetaQuitar={t('pos.quitarLinea', { producto: nombreLinea })}
                  onAbrir={() => setEditando(l.key)}
                  onQuitar={() => setPorQuitar(l.key)}
                />
              );
            })}
          </ul>
        )}
      </div>

      <footer className="border-t border-slate-800 bg-slate-950 px-4 py-4">
        {error && <p className="mb-3 text-sm text-rose-400">{error}</p>}
        {/* Above the total, as the receipt prints it, and in it: «Cobrar» has nothing more to ask. */}
        <CampoEnvio valor={envioTexto} onCambio={setEnvioTexto} error={errorEnvio} />
        <div className="mb-3 flex items-baseline justify-between" data-guia="pos-total">
          <span className="text-sm text-slate-400">{t('comun.total')}</span>
          {/*
            `key` on a figure is what makes the pulse replay: React swaps the node and the CSS
            animation starts again. Steadier than holding the previous value in state, and it
            cannot fall out of step with what is on screen.
          */}
          <span key={totalVenta} className="tabular late text-4xl font-bold tracking-tight">
            {formatMoney(totalVenta)}
          </span>
        </div>
        {sinPrecio > 0 && (
          <p className="mb-3 rounded-lg bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
            {t(puedeEditarPrecio ? 'pos.hayLineasSinPrecio' : 'pos.hayLineasSinPrecioPida', { n: sinPrecio })}
          </p>
        )}
        {rapidasPidenDueno && sinPrecio === 0 && (
          <p className="mb-3 rounded-lg bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
            {t('pos.rapidasPidenDueno', { maximo: formatMoney(centavos(config.ventaRapidaMaxima)) })}
          </p>
        )}
        {[...new Set(carrito.flatMap((l) => (l.productoId && vencidos.has(l.productoId) ? [l.productoId] : [])))].map((id) => {
          const producto = productos.find((p) => p.id === id);
          const nombreProducto = producto ? nombre(producto) : '';
          return (
            <p key={id} className="mb-3 rounded-lg bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
              {t(esMedicina(id) ? (puedeVenderVencido ? 'pos.medicinaVencidaDueno' : 'pos.medicinaVencida') : 'pos.perecederoVencido', {
                producto: nombreProducto,
                fecha: vencidos.get(id)!,
              })}
            </p>
          );
        })}
        {soloLoQueHay && !practica && !enLinea && (
          <p className="mb-3 rounded-lg bg-amber-500/10 px-3 py-2 text-sm text-amber-200" data-pos-sin-conexion>
            {t('pos.sinConexionVende')}
          </p>
        )}
        {faltasEnVista.length > 0 && (
          <div className="mb-3 rounded-lg bg-rose-500/10 px-3 py-2 text-sm text-rose-200" data-pos-falta>
            {faltasEnVista.map((f) => (
              <p key={f.productoId} data-falta={f.productoId}>
                {t(
                  f.conApartado
                    ? puedeVenderApartado
                      ? 'pos.faltaApartadoDueno'
                      : 'pos.faltaApartadoPideDueno'
                    : puedeVenderApartado
                      ? 'pos.faltaDueno'
                      : 'pos.faltaPideDueno',
                  { producto: f.producto, libre: f.libre, lleva: f.lleva, clientes: f.clientes },
                )}
              </p>
            ))}
          </div>
        )}
        {!soloLoQueHay && apartados.length > 0 && (
          <div className="mb-3 rounded-lg bg-rose-500/10 px-3 py-2 text-sm text-rose-200" data-pos-apartado>
            {apartados.map((a) => (
              <p key={a.productoId} data-toma-apartado={a.productoId}>
                {t(puedeVenderApartado ? 'pos.apartadoDueno' : 'pos.apartadoPideDueno', a)}
              </p>
            ))}
          </div>
        )}
        <button
          type="button"
          className="btn-primario h-16 w-full text-lg"
          data-guia="pos-cobrar"
          disabled={carrito.length === 0 || sinPrecio > 0 || errorEnvio !== null}
          onClick={() =>
            rapidasPidenDueno
              ? setPidiendoRapidas(true)
              : vencidaPideDueno
                ? setPidiendoVencida(true)
                : pideDueno
                  ? setPidiendoApartado(true)
                  : setCobrando(true)
          }
        >
          <Ico icono="caja" className="size-6" />
          {t('pos.cobrar')}
        </button>
      </footer>

      {enEdicion && (
        <EditorLinea
          linea={enEdicion}
          titulo={productoEnEdicion ? nombre(productoEnEdicion) : enEdicion.descripcion}
          unidades={enEdicion.productoId ? unidadesDe(unidades, enEdicion.productoId) : []}
          puedeEditarPrecio={puedeEditarPrecio}
          puedeCambiarPrecio={puedeCambiarPrecio}
          verCostos={verCostos}
          margenMinimoBp={config.precioMargenMinimoBp}
          // Another presentation at its own price on the line's list, as an order has it: switched to
          // the case, the line went on at the bottle's price, and nothing asked (2026-10-13).
          // An offer running on it is its price, as when it is rung up.
          precioDe={(unidadVentaId) => {
            const u = unidades.find((x) => x.id === unidadVentaId);
            return u ? (precioEnCaja(precios, ofertas, u, enEdicion.listaId ?? listaId, hoy())?.precio ?? null) : null;
          }}
          onCerrar={() => setEditando(null)}
          onQuitar={() => {
            // The question replaces the sheet rather than stacking on it: two sheets would both
            // answer Escape, and whichever closed first would unlock the page behind the other.
            setEditando(null);
            setPorQuitar(enEdicion.key);
          }}
          onGuardar={(cambios, requiereAutorizacion) => {
            if (requiereAutorizacion) setPidiendoPin({ key: enEdicion.key, cambios });
            else aplicar(enEdicion.key, cambios);
          }}
        />
      )}

      {aQuitar && (
        <Confirmar
          titulo={t('preguntar.quitarDeLaVenta')}
          mensaje={t('pos.confirmarQuitar', {
            producto: productoAQuitar ? nombre(productoAQuitar) : aQuitar.descripcion,
            total:
              aQuitar.precioUnitario > 0
                ? formatMoney(subtotalEnCaja(aQuitar))
                : t('pos.sinPrecio'),
          })}
          confirmar={t('comun.quitar')}
          icono="quitar"
          peligro
          onCerrar={() => setPorQuitar(null)}
          onConfirmar={() => quitar(aQuitar.key)}
        />
      )}

      {pidiendoPin && (
        <PedirPin
          motivo={t('pos.motivoAutorizacion')}
          onCancelar={() => setPidiendoPin(null)}
          onAutorizado={(usuarioId) => {
            aplicar(pidiendoPin.key, pidiendoPin.cambios, usuarioId);
            setPidiendoPin(null);
          }}
        />
      )}

      {pidiendoRapidas && (
        <PedirPin
          motivo={t('pos.motivoRapidas', {
            monto: formatMoney(rapidasSinAutorizar),
            maximo: formatMoney(centavos(config.ventaRapidaMaxima)),
          })}
          onCancelar={() => setPidiendoRapidas(false)}
          onAutorizado={(usuarioId) => {
            setCarrito((c) =>
              c.map((l) =>
                l.productoId === null && !l.autorizadoPor ? { ...l, autorizadoPor: usuarioId } : l,
              ),
            );
            setPidiendoRapidas(false);
            // The expired medicine, if any, is a different question with its own answer, and so is
            // what orders set aside.
            if (vencidaPideDueno) setPidiendoVencida(true);
            else if (pideDueno) setPidiendoApartado(true);
            else setCobrando(true);
          }}
        />
      )}

      {pidiendoVencida && (
        <PedirPin
          motivo={t('pos.motivoVencida', {
            productos: medicinaVencidaSinDueno
              .map((l) => {
                const p = productos.find((x) => x.id === l.productoId);
                return p ? nombre(p) : l.descripcion;
              })
              .join(', '),
          })}
          onCancelar={() => setPidiendoVencida(false)}
          onAutorizado={(usuarioId) => {
            const ids = new Set(medicinaVencidaSinDueno.map((l) => l.key));
            setCarrito((c) =>
              c.map((l) => {
                if (!ids.has(l.key)) return l;
                const { autorizadoEnElPedido: _delPedido, ...sinElDelPedido } = l;
                return { ...sinElDelPedido, autorizadoPor: usuarioId };
              }),
            );
            setPidiendoVencida(false);
            if (pideDueno) setPidiendoApartado(true);
            else setCobrando(true);
          }}
        />
      )}

      {/* The owner ringing it says yes with a tap; anybody else needs their PIN. Either is recorded. */}
      {pidiendoApartado &&
        (puedeVenderApartado ? (
          <Confirmar
            titulo={t(soloLoQueHay ? 'pos.venderDeMas' : 'pos.venderApartado')}
            mensaje={
              soloLoQueHay
                ? t('pos.motivoFaltaDueno', { productos: productosQueFaltan })
                : t('pos.motivoApartadoDueno', { productos: productosApartados })
            }
            confirmar={t('pos.venderIgual')}
            icono="caja"
            peligro
            onCerrar={() => setPidiendoApartado(false)}
            onConfirmar={() => {
              if (!sesion) return;
              autorizarApartado(sesion.usuarioId);
              setCobrando(true);
            }}
          />
        ) : (
          <PedirPin
            motivo={
              soloLoQueHay
                ? t('pos.motivoFalta', { productos: productosQueFaltan })
                : t('pos.motivoApartado', { productos: productosApartados })
            }
            onCancelar={() => setPidiendoApartado(false)}
            onAutorizado={(usuarioId) => {
              autorizarApartado(usuarioId);
              setPidiendoApartado(false);
              setCobrando(true);
            }}
          />
        ))}

      {pidiendoLista && (
        <PedirPin
          motivo={t('pos.motivoLista', { lista: nombreLista(pidiendoLista.listaId) ?? '' })}
          onCancelar={() => setPidiendoLista(null)}
          onAutorizado={(usuarioId) => {
            const { listaId: otra, key } = pidiendoLista;
            if (key) cambiarListaDeLinea(key, otra);
            else cambiarLista(otra);
            setListaAutorizadaPor(usuarioId);
            setListasAutorizadas((xs) => (xs.includes(otra) ? xs : [...xs, otra]));
            setPidiendoLista(null);
          }}
        />
      )}

      {eligiendoCliente && (
        <Modal
          titulo={t(clienteNuevo ? 'pos.clienteNuevo' : 'pos.cliente')}
          onCerrar={() => {
            setEligiendoCliente(false);
            setClienteNuevo(null);
            setFiarTrasElegir(false);
            setFiadoPedido(false);
          }}
        >
          {clienteNuevo ? (
            <ClienteNuevoEnCaja
              inicial={clienteNuevo}
              clientes={clientes}
              listas={activas}
              puedeDarCredito={puedeDarCredito}
              puedeElegirLista={puedeElegirLista}
              onGuardar={crearCliente}
              onElegir={elegirCliente}
              onVolver={() => setClienteNuevo(null)}
            />
          ) : (
            <ElegirCliente
              busquedaInicial={buscaCliente}
              clientes={clientes}
              pedidos={pedidos}
              puedeClientes={puedeClientes}
              nombreLista={nombreLista}
              onElegir={elegirCliente}
              onCargarPedido={(p) => cargarPedido(p)}
              onNuevo={(busqueda) => {
                // Kept for «Volver» from the form, which comes back to the list as it was left.
                setBuscaCliente(busqueda);
                setClienteNuevo(borradorDesdeBusqueda(busqueda));
              }}
            />
          )}
        </Modal>
      )}

      {porReemplazar && (
        <Confirmar
          titulo={t('pos.reemplazarTitulo')}
          mensaje={t('pos.reemplazarMensaje', {
            n: carrito.length,
            cliente: clientes.find((c) => c.id === porReemplazar.pedido.clienteId)?.nombre ?? t('pos.unCliente'),
          })}
          confirmar={t('pos.cargarElPedido')}
          icono="pedidos"
          onConfirmar={() => {
            cargarPedido(porReemplazar.pedido, porReemplazar.datos, true);
            setPorReemplazar(null);
          }}
          onCerrar={() => setPorReemplazar(null)}
        />
      )}

      {noSeCarga && (
        <Modal
          titulo={t('pedidos.noSeCarga')}
          onCerrar={() => setNoSeCarga(null)}
          pie={
            <div className="flex gap-3">
              <Link to="/pedidos" onClick={atrasHasta('/pedidos')} className="btn-secundario min-w-0 flex-1 px-3">
                <Ico icono="pedidos" />
                {t('pedidos.verLista')}
              </Link>
              <button type="button" className="btn-primario min-w-0 flex-1 px-3" onClick={() => setNoSeCarga(null)}>
                <Ico icono="listo" />
                {t('comun.listo')}
              </button>
            </div>
          }
        >
          <p className="text-sm text-slate-300" data-pedido-no-se-carga>
            {noSeCarga}
          </p>
        </Modal>
      )}

      {abonando && cliente && (
        <AbonoModal cliente={cliente} negocio={config.negocioNombre} onCerrar={() => setAbonando(false)} />
      )}

      {rapida && (
        <VentaRapida
          avisarDesde={
            puedeCambiarPrecio ? null : centavos(Math.max(0, config.ventaRapidaMaxima - rapidasSinAutorizar))
          }
          maximo={centavos(config.ventaRapidaMaxima)}
          onCerrar={() => setRapida(false)}
          onAgregar={(descripcion, precio) => {
            setCarrito((c) => [
              ...c,
              {
                key: newId(),
                productoId: null,
                descripcion,
                unidad: null,
                cantidad: fromUnits(1),
                precioLista: precio,
                precioUnitario: precio,
                motivoOverride: null,
                autorizadoPor: null,
                costoReferencia: ZERO_COST,
              },
            ]);
            setRapida(false);
          }}
        />
      )}

      {cobrando && (
        <PanelCobro
          totalExacto={subtotal}
          envio={envio}
          config={config}
          cliente={cliente}
          // Not waiting on `cliente`: a client added a moment ago is on the sale before the list
          // of clients has caught up, and the panel must still open on fiado.
          formaInicial={fiadoPedido ? 'credito' : undefined}
          onElegirCliente={() => {
            setCobrando(false);
            setFiarTrasElegir(true);
            setFiadoPedido(true);
            setEligiendoCliente(true);
          }}
          onCerrar={() => {
            setCobrando(false);
            setFiadoPedido(false);
          }}
          onCobrar={cobrar}
          onRedondeo={setRedondeoCobro}
          {...(confirmando ? { textoOcupado: t('pos.confirmando') } : {})}
        />
      )}

      {recibo && (
        <Modal
          titulo={t('pos.venta', { ticket: recibo.ticket })}
          onCerrar={guardarRecibo}
          cedeAlLector
          pie={
            <div className="flex flex-col gap-3">
              <div className="flex gap-2">
                {/* Straight to the client's own chat when the sale was to somebody with a phone. */}
                <a
                  className="btn-secundario min-w-0 flex-1 px-3 text-sm"
                  href={enlaceWhatsApp(recibo.texto, recibo.telefono)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Ico icono="whatsapp" />
                  WhatsApp
                </a>
                <button
                  type="button"
                  className="btn-secundario min-w-0 flex-1 px-3 text-sm"
                  onClick={() => imprimirTexto(recibo.texto)}
                >
                  <Ico icono="imprimir" />
                  {t('comun.imprimir')}
                </button>
              </div>
              <button type="button" className="btn-primario w-full" onClick={guardarRecibo}>
                <Ico icono={recibo.aPedidos ? 'pedidos' : 'vender'} />
                {t(recibo.aPedidos ? 'pos.volverAPedidos' : 'pos.siguienteVenta')}
              </button>
            </div>
          }
        >
          {recibo.sinConfirmar && (
            <p className="mb-3 rounded-lg bg-amber-500/10 px-3 py-2 text-sm text-amber-200" data-recibo-sin-confirmar>
              {t('pos.sinConfirmar')}
            </p>
          )}
          <pre className="tabular overflow-x-auto whitespace-pre rounded-xl bg-slate-950 p-4 text-xs leading-relaxed">
            {recibo.texto}
          </pre>
        </Modal>
      )}
    </div>
  );
}

/**
 * Who the sale is for: the client list over the till, holding its own search.
 *
 * Its search used to be the till's own state, so every key typed in it re-drew the whole till
 * behind the sheet, the way the new client's form did (`ClienteNuevoEnCaja`). A name typed here,
 * to find somebody or to add them, was the slowest typing left at the counter with the processor
 * slowed six times: now a key re-draws the list.
 */
function ElegirCliente({
  busquedaInicial,
  clientes,
  pedidos,
  puedeClientes,
  nombreLista,
  onElegir,
  onCargarPedido,
  onNuevo,
}: {
  busquedaInicial: string;
  clientes: readonly Cliente[];
  pedidos: readonly Pedido[];
  puedeClientes: boolean;
  nombreLista: (id: string | null | undefined) => string | null;
  onElegir: (id: string | null) => void;
  onCargarPedido: (p: Pedido) => void;
  /** Somebody not in the file: added from here, with what was typed in place. */
  onNuevo: (busqueda: string) => void;
}) {
  const t = useT();
  const [busca, setBusca] = useState(busquedaInicial);
  // Thirty at a time and the next as the sheet is scrolled, to the last client: it drew thirty and
  // stopped, about «C», like «Productos» did (the owner, 2026-10-12).
  const [cuantos, setCuantos] = useState(CLIENTES_A_LA_VEZ);
  const [cuantosPedidos, setCuantosPedidos] = useState(PEDIDOS_A_LA_VEZ);
  const porEntregar = useMemo(() => pedidos.slice().sort((a, b) => a.fechaEntrega.localeCompare(b.fechaEntrega)), [pedidos]);
  const hallados = useMemo(
    () =>
      clientes
        .filter((c) => c.activo)
        .filter((c) => !busca.trim() || coincide(c.nombre, busca) || coincideTelefono(c.telefono, busca))
        .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')),
    [clientes, busca],
  );
  return (
    <div data-guia="pos-clientes">
      <Campo
        etiqueta={t('comun.buscar')}
        value={busca}
        onChange={(e) => {
          setBusca(e.target.value);
          setCuantos(CLIENTES_A_LA_VEZ);
        }}
        placeholder={t('pos.nombreOTelefono')}
        autoComplete="off"
        autoFocus
      />

      {/* Not in the file yet: add them from here, with whatever was typed already in place. */}
      {puedeClientes && (
        <button
          type="button"
          className="btn-secundario mt-3 h-12 min-h-0 w-full text-sm"
          onClick={() => onNuevo(busca)}
        >
          <Ico icono="clienteNuevo" className="size-4 shrink-0" />
          <span className="truncate">
            {busca.trim() && !pareceTelefono(busca)
              ? t('pos.agregarComo', { nombre: busca.trim() })
              : t('pos.clienteNuevo')}
          </span>
        </button>
      )}

      {pedidos.length > 0 && !busca.trim() && (
        <div className="mt-4" data-pos-pedidos>
          <h3 className="etiqueta">{t('pos.pedidosPorEntregar')}</h3>
          <ul className="mt-1 flex flex-col gap-2">
            {porEntregar.slice(0, cuantosPedidos).map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 rounded-xl bg-slate-800 px-4 py-3 text-left transition hover:bg-slate-700"
                  onClick={() => onCargarPedido(p)}
                  data-sin-icono="fila"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">
                      {clientes.find((c) => c.id === p.clienteId)?.nombre ??
                        t('pos.cliente')}
                    </span>
                    <span className="block text-xs text-slate-500">
                      {t('pos.entrega', { fecha: p.fechaEntrega, n: p.lineas.length })}
                    </span>
                  </span>
                  <span className="tabular shrink-0 font-semibold">{formatMoney(totalConEnvio(p))}</span>
                </button>
              </li>
            ))}
          </ul>
          {/* Tapped, not scrolled into: the clients come under them. */}
          {porEntregar.length > cuantosPedidos && (
            <button
              type="button"
              className="btn-secundario mt-2 w-full text-sm"
              onClick={() => setCuantosPedidos((n) => n + PEDIDOS_A_LA_VEZ)}
              data-pos-pedidos-mas
            >
              <Ico icono="desplegar" />
              {t('comun.verMas')}
            </button>
          )}
        </div>
      )}

      <div className="mt-4">
        <h3 className="etiqueta">{t('pos.clientes')}</h3>
        <ul className="mt-1 flex flex-col gap-1">
          <li>
            <button
              type="button"
              className="w-full rounded-lg px-3 py-2 text-left text-sm text-slate-400 hover:bg-slate-800"
              onClick={() => onElegir(null)}
              data-sin-icono="fila"
            >
              {t('pos.sinCliente')}
            </button>
          </li>
          {hallados.slice(0, cuantos).map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="w-full rounded-lg px-3 py-2 text-left hover:bg-slate-800"
                onClick={() => onElegir(c.id)}
                data-sin-icono="fila"
                data-pos-cliente
              >
                <span className="block truncate">{c.nombre}</span>
                <span className="block text-xs text-slate-500">
                  {nombreLista(c.listaId) ?? t('pos.listaPredeterminada')}
                  {(c.saldo ?? 0) > 0 && (
                    <span className="tabular text-amber-300">
                      {' · '}
                      {t('fiado.debe', { monto: formatMoney(c.saldo!) })}
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
        {hallados.length > cuantos && (
          <VerMasAlBajar quedan={hallados.length - cuantos} onMas={() => setCuantos((n) => n + CLIENTES_A_LA_VEZ)} />
        )}
      </div>
    </div>
  );
}

/**
 * A new client's form, over the till, holding what is typed in it itself.
 *
 * Its draft used to be the till's own state, so every key typed in it re-drew the whole till
 * behind the sheet: the sale, the lists, the customer's screen. With the processor six times
 * slower, about a budget phone, a cédula's fourteen characters took over a second to go in, the
 * letters trailing the thumb. Now a key re-draws the form, and the till hears about the client
 * once, when it is saved.
 */
function ClienteNuevoEnCaja({
  inicial,
  clientes,
  listas,
  puedeDarCredito,
  puedeElegirLista,
  onGuardar,
  onElegir,
  onVolver,
}: {
  inicial: BorradorCliente;
  /** Everybody already in the books: a name already there is that client, not a new one. */
  clientes: readonly Cliente[];
  listas: ReturnType<typeof ordenarListas>;
  puedeDarCredito: boolean;
  puedeElegirLista: boolean;
  /** Saves them and puts them on the sale: the books' reason when they would not. */
  onGuardar: (b: BorradorCliente) => Promise<string | null>;
  /** The client already called that, put on the sale instead. */
  onElegir: (clienteId: string) => void;
  onVolver: () => void;
}) {
  const t = useT();
  const [b, setB] = useState(inicial);
  const [error, setError] = useState<string | null>(null);
  /**
   * Pressed once: a second press while the first is saved made the same client twice. Nor is one
   * already called that saved again, which split their fiado in two; and what went wrong is said
   * here, where it was said behind the sheet (2026-10-13), as a new order's client has it.
   */
  const enviado = useRef(false);
  const yaEsta = clienteLlamado(clientes, b.nombre);

  async function guardar() {
    if (enviado.current || yaEsta) return;
    enviado.current = true;
    setError(null);
    try {
      const rechazo = await onGuardar(b);
      if (rechazo) setError(rechazo);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('pos.noSePudoRegistrar'));
    }
    enviado.current = false;
  }

  return (
    <>
      <FormularioCliente
        b={b}
        setB={setB}
        listas={listas}
        puedeDarCredito={puedeDarCredito}
        puedeElegirLista={puedeElegirLista}
        conCedula
        compacto
        telefonoObligatorio
        yaEsta={yaEsta}
        onElegir={onElegir}
        onGuardar={() => void guardar()}
        extra={
          <button type="button" className="btn-secundario px-4" onClick={onVolver}>
            <Ico icono="atras" />
            {t('comun.volver')}
          </button>
        }
      />
      {error && (
        <p role="alert" className="mt-3 text-sm text-rose-400">
          {error}
        </p>
      )}
    </>
  );
}

/**
 * A quick sale's line, made bigger: the owner's yes was to its amount, so a quick line whose
 * total goes up is asked for again at «Cobrar». A product's line keeps its authorisation at any
 * quantity, since what the owner answered there is «¿a este precio?».
 */
function rapidaQueSube(antes: LineaCarrito, despues: LineaCarrito): boolean {
  return (
    antes.productoId === null &&
    lineTotal(despues.precioUnitario, despues.cantidad) > lineTotal(antes.precioUnitario, antes.cantidad)
  );
}

function VentaRapida({
  avisarDesde,
  maximo,
  onAgregar,
  onCerrar,
}: {
  /** Past this, the ticket's quick sales take the owner's PIN at «Cobrar». Null: the owner. */
  avisarDesde: Centavos | null;
  maximo: Centavos;
  onAgregar: (descripcion: string, precio: Centavos) => void;
  onCerrar: () => void;
}) {
  const t = useT();
  const [descripcion, setDescripcion] = useState('');
  const [precio, setPrecio] = useState('');
  const monto = parseMoney(precio);
  const valido = descripcion.trim().length > 0 && monto !== null && monto > 0;
  const pideDueno = avisarDesde !== null && monto !== null && monto > avisarDesde;

  return (
    <Modal
      titulo={t('pos.ventaRapida')}
      onCerrar={onCerrar}
      pie={
        <button
          type="button"
          className="btn-primario w-full"
          disabled={!valido}
          onClick={() => onAgregar(descripcion.trim(), monto!)}
        >
          <Ico icono="agregar" />
          {t('comun.agregar')}
        </button>
      }
    >
      <p className="mb-4 text-sm text-slate-400">
        {t('pos.ventaRapidaExplicacion')}
      </p>
      <div className="flex flex-col gap-4">
        <Campo
          maxLength={120}
          etiqueta={t('pos.descripcion')}
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          autoFocus
          autoComplete="off"
          placeholder={t('pos.ejemploVentaRapida')}
        />
        <Campo
          etiqueta={t('comun.precio')}
          inputMode="decimal"
          className="tabular text-lg"
          value={precio}
          onChange={(e) => setPrecio(e.target.value)}
        />
        {pideDueno && (
          <p className="rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
            {t('pos.rapidaPideDueno', { maximo: formatMoney(maximo) })}
          </p>
        )}
      </div>
    </Modal>
  );
}
